import React, { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAdminCourses, createCourse, updateCourse, uploadCourseBanner, deleteCourseBanner, uploadCourseBadgeBg, deleteCourseBadgeBg, uploadCourseEmailBanner, deleteCourseEmailBanner, uploadCourseProgramma, deleteCourseProgramma, updateCourseFields } from '../../api/courses';
import type { Course, RegistrationField, RegistrationFieldType } from '../../types';

interface CourseFormData {
  name: string;
  slug: string;
  description: string;
  active: boolean;
  contactEmail: string;
  showSpeakers: boolean;
}

// Remove ASCII control characters (0x00–0x1F) except tab, newline, carriage return
function stripControlChars(s: string): string {
  return s.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');
}

function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[àáâãäå]/g, 'a')
    .replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõö]/g, 'o')
    .replace(/[ùúûü]/g, 'u')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

const BACKEND_URL = 'http://localhost:3001';

const FIELD_TYPE_LABELS: Record<RegistrationFieldType, string> = {
  text: 'Testo', textarea: 'Area testo', select: 'Lista', checkbox: 'Checkbox', number: 'Numero', date: 'Data',
};

function keyify(label: string): string {
  return label.toLowerCase()
    .replace(/[àáâãäå]/g, 'a').replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i').replace(/[òóôõö]/g, 'o').replace(/[ùúûü]/g, 'u')
    .replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
}

const EMPTY_FIELD: Partial<RegistrationField> = { type: 'text', required: false, label: '', key: '', placeholder: '', options: [], showIf: undefined };

function FieldsEditorModal({ course, onClose, onSaved }: { course: Course; onClose: () => void; onSaved: () => void }) {
  const [fields, setFields] = useState<RegistrationField[]>(course.registrationFields ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newField, setNewField] = useState<Partial<RegistrationField>>({ ...EMPTY_FIELD });
  const [showAdd, setShowAdd] = useState(false);
  const [optionsText, setOptionsText] = useState('');

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      await updateCourseFields(course.id, fields);
      onSaved(); onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Errore nel salvataggio');
    } finally { setSaving(false); }
  };

  const commitAdd = () => {
    if (!newField.label?.trim() || !newField.key?.trim()) return;
    const opts = newField.type === 'select' ? optionsText.split('\n').map(s => s.trim()).filter(Boolean) : undefined;
    const showIf = newField.showIf?.key?.trim() ? { key: newField.showIf.key, value: newField.showIf.value ?? '' } : undefined;
    setFields(f => [...f, { key: newField.key!, label: newField.label!, type: newField.type ?? 'text', required: newField.required ?? false, placeholder: newField.placeholder || undefined, options: opts, showIf }]);
    setNewField({ ...EMPTY_FIELD }); setOptionsText(''); setShowAdd(false);
  };

  const moveField = (i: number, dir: -1 | 1) => {
    setFields(f => { const a = [...f]; [a[i], a[i + dir]] = [a[i + dir], a[i]]; return a; });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 620 }}>
        <h2 className="modal-title">Campi personalizzati — {course.name}</h2>
        {error && <div className="alert alert-danger">{error}</div>}

        {fields.length === 0 && !showAdd && (
          <p style={{ color: 'var(--gray-400)', fontSize: '0.875rem', marginBottom: '1rem' }}>
            Nessun campo aggiuntivo. I campi standard (nome, cognome, email, azienda) sono sempre presenti.
          </p>
        )}

        {fields.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
            {fields.map((f, i) => (
              <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--gray-50)', borderRadius: 8, padding: '0.625rem 0.875rem', border: '1px solid var(--gray-200)' }}>
                <span style={{ flex: 1, fontWeight: 500, fontSize: '0.875rem' }}>{f.label}</span>
                <span className="badge badge-gray" style={{ fontSize: '0.75rem' }}>{FIELD_TYPE_LABELS[f.type]}</span>
                {f.required && <span className="badge badge-blue" style={{ fontSize: '0.75rem' }}>Obbligatorio</span>}
                {f.showIf && <span className="badge badge-gray" style={{ fontSize: '0.7rem', opacity: 0.7 }}>se {f.showIf.key}={f.showIf.value}</span>}
                <code style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{f.key}</code>
                <button onClick={() => moveField(i, -1)} disabled={i === 0} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', fontSize: '0.875rem', padding: '0 0.2rem' }}>▲</button>
                <button onClick={() => moveField(i, 1)} disabled={i === fields.length - 1} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', fontSize: '0.875rem', padding: '0 0.2rem' }}>▼</button>
                <button onClick={() => setFields(f => f.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: '0.875rem', padding: '0 0.2rem' }}>✕</button>
              </div>
            ))}
          </div>
        )}

        {showAdd ? (
          <div style={{ background: 'var(--gray-50)', borderRadius: 8, padding: '1rem', border: '1px solid var(--gray-200)', marginBottom: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontSize: '0.8125rem' }}>Etichetta *</label>
                <input className="form-control" value={newField.label ?? ''} onChange={e => setNewField(f => ({ ...f, label: e.target.value, key: keyify(e.target.value) }))} placeholder="Es. Codice Fiscale" />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontSize: '0.8125rem' }}>Chiave (key) *</label>
                <input className="form-control" value={newField.key ?? ''} onChange={e => setNewField(f => ({ ...f, key: e.target.value }))} placeholder="es. codice_fiscale" />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontSize: '0.8125rem' }}>Tipo</label>
                <select className="form-control" value={newField.type ?? 'text'} onChange={e => setNewField(f => ({ ...f, type: e.target.value as RegistrationFieldType }))}>
                  {(Object.keys(FIELD_TYPE_LABELS) as RegistrationFieldType[]).map(t => (
                    <option key={t} value={t}>{FIELD_TYPE_LABELS[t]}</option>
                  ))}
                </select>
              </div>
              {newField.type !== 'checkbox' && newField.type !== 'select' && (
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.8125rem' }}>Placeholder</label>
                  <input className="form-control" value={newField.placeholder ?? ''} onChange={e => setNewField(f => ({ ...f, placeholder: e.target.value }))} />
                </div>
              )}
            </div>
            {newField.type === 'select' && (
              <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                <label className="form-label" style={{ fontSize: '0.8125rem' }}>Opzioni (una per riga)</label>
                <textarea className="form-control" rows={3} value={optionsText} onChange={e => setOptionsText(e.target.value)} placeholder="Opzione 1&#10;Opzione 2&#10;Opzione 3" style={{ resize: 'vertical' }} />
              </div>
            )}
            <div className="form-check" style={{ marginBottom: '0.75rem' }}>
              <input type="checkbox" id="req" className="form-check-input" checked={newField.required ?? false} onChange={e => setNewField(f => ({ ...f, required: e.target.checked }))} />
              <label htmlFor="req" className="form-check-label" style={{ fontSize: '0.875rem' }}>Campo obbligatorio</label>
            </div>
            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
              <label className="form-label" style={{ fontSize: '0.8125rem' }}>Visibile solo se (condizionale)</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '0.5rem', alignItems: 'center' }}>
                <select className="form-control" value={newField.showIf?.key ?? ''}
                  onChange={e => setNewField(f => ({ ...f, showIf: e.target.value ? { key: e.target.value, value: f.showIf?.value ?? '' } : undefined }))}>
                  <option value="">— nessuna condizione —</option>
                  {fields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
                <span style={{ fontSize: '0.8125rem', color: 'var(--gray-400)', padding: '0 0.25rem' }}>=</span>
                <input className="form-control" value={newField.showIf?.value ?? ''}
                  placeholder="valore atteso"
                  disabled={!newField.showIf?.key}
                  onChange={e => setNewField(f => f.showIf ? { ...f, showIf: { ...f.showIf, value: e.target.value } } : f)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={commitAdd} disabled={!newField.label?.trim() || !newField.key?.trim()}>Aggiungi</button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setShowAdd(false); setNewField({ ...EMPTY_FIELD }); setOptionsText(''); }}>Annulla</button>
            </div>
          </div>
        ) : (
          <button className="btn btn-ghost btn-sm" style={{ marginBottom: '1.25rem' }} onClick={() => setShowAdd(true)}>+ Aggiungi campo</button>
        )}

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Salvataggio...' : 'Salva campi'}</button>
        </div>
      </div>
    </div>
  );
}

function CourseModal({
  course,
  onClose,
  onSaved,
}: {
  course?: Course;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!course;
  const [form, setForm] = useState<CourseFormData>({
    name: course?.name ?? '',
    slug: course?.slug ?? '',
    description: course?.description ?? '',
    active: course?.active ?? true,
    contactEmail: course?.contactEmail ?? '',
    showSpeakers: course?.showSpeakers ?? false,
  });
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [bannerPreview, setBannerPreview] = useState<string | null>(
    course?.bannerUrl ? `${BACKEND_URL}${course.bannerUrl}` : null
  );
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerDeleted, setBannerDeleted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [badgeBgPreview, setBadgeBgPreview] = useState<string | null>(
    course?.badgeBgUrl ? `${BACKEND_URL}${course.badgeBgUrl}` : null
  );
  const [badgeBgFile, setBadgeBgFile] = useState<File | null>(null);
  const [badgeBgDeleted, setBadgeBgDeleted] = useState(false);

  const [emailBannerPreview, setEmailBannerPreview] = useState<string | null>(
    course?.emailBannerUrl ? `${BACKEND_URL}${course.emailBannerUrl}` : null
  );
  const [emailBannerFile, setEmailBannerFile] = useState<File | null>(null);
  const [emailBannerDeleted, setEmailBannerDeleted] = useState(false);
  const emailBannerInputRef = useRef<HTMLInputElement>(null);
  const badgeBgInputRef = useRef<HTMLInputElement>(null);

  const [programmaName, setProgrammaName] = useState<string | null>(
    course?.programmaUrl ? course.programmaUrl.split('/').pop() ?? 'programma.pdf' : null
  );
  const [programmaFile, setProgrammaFile] = useState<File | null>(null);
  const [programmaDeleted, setProgrammaDeleted] = useState(false);
  const programmaInputRef = useRef<HTMLInputElement>(null);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setForm((f) => ({ ...f, name, slug: isEdit ? f.slug : slugify(name) }));
  };

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerFile(file);
    setBannerDeleted(false);
    setBannerPreview(URL.createObjectURL(file));
  };

  const handleRemoveBanner = () => {
    setBannerFile(null);
    setBannerPreview(null);
    setBannerDeleted(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleBadgeBgChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBadgeBgFile(file);
    setBadgeBgDeleted(false);
    setBadgeBgPreview(URL.createObjectURL(file));
  };

  const handleRemoveBadgeBg = () => {
    setBadgeBgFile(null);
    setBadgeBgPreview(null);
    setBadgeBgDeleted(true);
    if (badgeBgInputRef.current) badgeBgInputRef.current.value = '';
  };

  const handleEmailBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEmailBannerFile(file);
    setEmailBannerDeleted(false);
    setEmailBannerPreview(URL.createObjectURL(file));
  };

  const handleRemoveEmailBanner = () => {
    setEmailBannerFile(null);
    setEmailBannerPreview(null);
    setEmailBannerDeleted(true);
    if (emailBannerInputRef.current) emailBannerInputRef.current.value = '';
  };

  const handleProgrammaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProgrammaFile(file);
    setProgrammaDeleted(false);
    setProgrammaName(file.name);
  };

  const handleRemoveProgramma = () => {
    setProgrammaFile(null);
    setProgrammaName(null);
    setProgrammaDeleted(true);
    if (programmaInputRef.current) programmaInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);
    try {
      const cleanForm = {
        ...form,
        name: stripControlChars(form.name),
        slug: stripControlChars(form.slug),
        description: stripControlChars(form.description),
        contactEmail: stripControlChars(form.contactEmail),
      };
      let saved: Course;
      if (isEdit) {
        saved = await updateCourse(course.id, cleanForm);
      } else {
        saved = await createCourse(cleanForm);
      }
      if (bannerFile) {
        await uploadCourseBanner(saved.id, bannerFile);
      } else if (bannerDeleted && isEdit && course.bannerUrl) {
        await deleteCourseBanner(saved.id);
      }
      if (badgeBgFile) {
        await uploadCourseBadgeBg(saved.id, badgeBgFile);
      } else if (badgeBgDeleted && isEdit && course.badgeBgUrl) {
        await deleteCourseBadgeBg(saved.id);
      }
      if (emailBannerFile) {
        await uploadCourseEmailBanner(saved.id, emailBannerFile);
      } else if (emailBannerDeleted && isEdit && course.emailBannerUrl) {
        await deleteCourseEmailBanner(saved.id);
      }
      if (programmaFile) {
        await uploadCourseProgramma(saved.id, programmaFile);
      } else if (programmaDeleted && isEdit && course.programmaUrl) {
        await deleteCourseProgramma(saved.id);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Errore nel salvataggio');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
        <h2 className="modal-title">{isEdit ? 'Modifica corso' : 'Nuovo corso'}</h2>

        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Nome <span className="required">*</span></label>
            <input
              type="text"
              className="form-control"
              value={form.name}
              onChange={handleNameChange}
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label">Slug <span className="required">*</span></label>
            <input
              type="text"
              className="form-control"
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              required
              pattern="[a-z0-9-]+"
            />
            <span className="form-hint">Solo lettere minuscole, numeri e trattini. Usato nell'URL.</span>
          </div>

          <div className="form-group">
            <label className="form-label">Descrizione</label>
            <textarea
              className="form-control"
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Banner / Testata</label>
            {bannerPreview ? (
              <div style={{ marginBottom: '0.75rem', position: 'relative' }}>
                <img
                  src={bannerPreview}
                  alt="Banner"
                  style={{
                    width: '100%',
                    height: '140px',
                    objectFit: 'cover',
                    borderRadius: 'var(--border-radius)',
                    border: '1px solid var(--gray-200)',
                    display: 'block',
                  }}
                />
                <button
                  type="button"
                  onClick={handleRemoveBanner}
                  style={{
                    position: 'absolute',
                    top: '0.5rem',
                    right: '0.5rem',
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '0.2rem 0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                  }}
                >
                  Rimuovi
                </button>
              </div>
            ) : (
              <div
                style={{
                  border: '2px dashed var(--gray-300)',
                  borderRadius: 'var(--border-radius)',
                  padding: '1.5rem',
                  textAlign: 'center',
                  marginBottom: '0.5rem',
                  cursor: 'pointer',
                  color: 'var(--gray-400)',
                  fontSize: '0.875rem',
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                Clicca per caricare un banner
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              onChange={handleBannerChange}
            />
            {!bannerPreview && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => fileInputRef.current?.click()}>
                Scegli immagine
              </button>
            )}
            <span className="form-hint" style={{ display: 'block', marginTop: '0.25rem' }}>
              JPG, PNG o WebP — max 5MB. Proporzioni consigliate: 3:1 (es. 1200×400px)
            </span>
          </div>

          <div className="form-group">
            <label className="form-label">Sfondo Badge (solo per corsi in presenza)</label>
            {badgeBgPreview ? (
              <div style={{ marginBottom: '0.75rem', position: 'relative' }}>
                <img
                  src={badgeBgPreview}
                  alt="Sfondo Badge"
                  style={{ width: '100%', height: '140px', objectFit: 'cover', borderRadius: 'var(--border-radius)', border: '1px solid var(--gray-200)', display: 'block' }}
                />
                <button
                  type="button"
                  onClick={handleRemoveBadgeBg}
                  style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.75rem' }}
                >
                  Rimuovi
                </button>
              </div>
            ) : (
              <div
                style={{ border: '2px dashed var(--gray-300)', borderRadius: 'var(--border-radius)', padding: '1.5rem', textAlign: 'center', marginBottom: '0.5rem', cursor: 'pointer', color: 'var(--gray-400)', fontSize: '0.875rem' }}
                onClick={() => badgeBgInputRef.current?.click()}
              >
                Clicca per caricare lo sfondo del badge
              </div>
            )}
            <input ref={badgeBgInputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handleBadgeBgChange} />
            {!badgeBgPreview && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => badgeBgInputRef.current?.click()}>
                Scegli immagine
              </button>
            )}
            <span className="form-hint" style={{ display: 'block', marginTop: '0.25rem' }}>
              JPG, PNG o WebP — max 5MB. Proporzioni badge: A6 verticale (105×148mm) o simile
            </span>
          </div>

          <div className="form-group">
            <label className="form-label">Testata email</label>
            {emailBannerPreview ? (
              <div style={{ marginBottom: '0.75rem', position: 'relative' }}>
                <img
                  src={emailBannerPreview}
                  alt="Email banner"
                  style={{ width: '100%', height: '140px', objectFit: 'cover', borderRadius: 'var(--border-radius)', border: '1px solid var(--gray-200)', display: 'block' }}
                />
                <button
                  type="button"
                  onClick={handleRemoveEmailBanner}
                  style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.75rem' }}
                >
                  Rimuovi
                </button>
              </div>
            ) : (
              <div
                style={{ border: '2px dashed var(--gray-300)', borderRadius: 'var(--border-radius)', padding: '1.5rem', textAlign: 'center', marginBottom: '0.5rem', cursor: 'pointer', color: 'var(--gray-400)', fontSize: '0.875rem' }}
                onClick={() => emailBannerInputRef.current?.click()}
              >
                Clicca per caricare la testata email
              </div>
            )}
            <input ref={emailBannerInputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handleEmailBannerChange} />
            {!emailBannerPreview && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => emailBannerInputRef.current?.click()}>
                Scegli immagine
              </button>
            )}
            <span className="form-hint" style={{ display: 'block', marginTop: '0.25rem' }}>
              JPG, PNG o WebP — max 5MB. Larghezza consigliata: 600px. Sostituisce il blocco blu nelle email.
            </span>
          </div>

          <div className="form-group">
            <label className="form-label">Programma (PDF scaricabile)</label>
            {programmaName ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 'var(--border-radius)', padding: '0.75rem 1rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.25rem' }}>📄</span>
                <span style={{ flex: 1, fontSize: '0.875rem', color: 'var(--gray-700)', wordBreak: 'break-all' }}>{programmaName}</span>
                <button type="button" onClick={handleRemoveProgramma} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: '0.875rem', fontWeight: 600 }}>Rimuovi</button>
              </div>
            ) : (
              <div
                style={{ border: '2px dashed var(--gray-300)', borderRadius: 'var(--border-radius)', padding: '1.5rem', textAlign: 'center', marginBottom: '0.5rem', cursor: 'pointer', color: 'var(--gray-400)', fontSize: '0.875rem' }}
                onClick={() => programmaInputRef.current?.click()}
              >
                Clicca per caricare il programma PDF
              </div>
            )}
            <input ref={programmaInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={handleProgrammaChange} />
            {!programmaName && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => programmaInputRef.current?.click()}>
                Scegli PDF
              </button>
            )}
            <span className="form-hint" style={{ display: 'block', marginTop: '0.25rem' }}>
              Solo PDF — max 20MB. Sarà scaricabile nella pagina di registrazione.
            </span>
          </div>

          <div className="form-group">
            <label className="form-label">Email di contatto (footer email)</label>
            <input
              type="email"
              className="form-control"
              value={form.contactEmail}
              onChange={(e) => setForm((f) => ({ ...f, contactEmail: e.target.value }))}
              placeholder="formazione@esempio.it"
            />
            <span className="form-hint">Appare nel footer delle email al posto dell'indirizzo generico TUPLA.</span>
          </div>

          <div className="form-group">
            <div className="form-check">
              <input
                type="checkbox"
                id="active"
                className="form-check-input"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              />
              <label htmlFor="active" className="form-check-label">
                Corso attivo (visibile nel form di registrazione)
              </label>
            </div>
          </div>

          <div className="form-group">
            <div className="form-check">
              <input
                type="checkbox"
                id="showSpeakers"
                className="form-check-input"
                checked={form.showSpeakers}
                onChange={(e) => setForm((f) => ({ ...f, showSpeakers: e.target.checked }))}
              />
              <label htmlFor="showSpeakers" className="form-check-label">
                Mostra colonna relatori nella pagina di registrazione
              </label>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Annulla</button>
            <button type="submit" className="btn btn-primary" disabled={isSaving}>
              {isSaving ? 'Salvataggio...' : isEdit ? 'Salva modifiche' : 'Crea corso'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminCoursesPage() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | undefined>();
  const [fieldsEditingCourse, setFieldsEditingCourse] = useState<Course | undefined>();

  const { data: courses = [], isLoading } = useQuery({
    queryKey: ['admin-courses'],
    queryFn: getAdminCourses,
  });

  const handleSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-courses'] });
  };

  const handleToggleActive = async (course: Course) => {
    try {
      await updateCourse(course.id, { active: !course.active });
      queryClient.invalidateQueries({ queryKey: ['admin-courses'] });
    } catch (err: any) {
      alert(err?.response?.data?.error ?? 'Errore');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Corsi</h1>
        <button
          className="btn btn-primary"
          onClick={() => { setEditingCourse(undefined); setShowModal(true); }}
        >
          + Nuovo corso
        </button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {isLoading ? (
          <div className="loading-container"><div className="spinner" /></div>
        ) : courses.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--gray-500)' }}>
            Nessun corso. Crea il primo corso!
          </div>
        ) : (
          <div className="table-container" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: '88px' }}>Banner</th>
                  <th>Nome</th>
                  <th>Slug</th>
                  <th>Descrizione</th>
                  <th>Modalità</th>
                  <th>Stato</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {courses.map((course) => (
                  <tr key={course.id}>
                    <td>
                      {course.bannerUrl ? (
                        <img
                          src={`${BACKEND_URL}${course.bannerUrl}`}
                          alt=""
                          style={{ width: 72, height: 40, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--gray-200)', display: 'block' }}
                        />
                      ) : (
                        <div style={{ width: 72, height: 40, background: 'var(--gray-100)', borderRadius: 4, border: '1px dashed var(--gray-300)' }} />
                      )}
                    </td>
                    <td style={{ fontWeight: 500 }}>{course.name}</td>
                    <td>
                      <code style={{ fontSize: '0.8125rem', color: 'var(--gray-600)' }}>{course.slug}</code>
                    </td>
                    <td style={{ color: 'var(--gray-600)', maxWidth: '260px' }}>
                      <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: '0.875rem' }}>
                        {course.description ?? '—'}
                      </span>
                    </td>
                    <td>
                    </td>
                    <td>
                      <button
                        className={`badge ${course.active ? 'badge-green' : 'badge-gray'}`}
                        style={{ cursor: 'pointer', border: 'none' }}
                        onClick={() => handleToggleActive(course)}
                        title="Clicca per cambiare stato"
                      >
                        {course.active ? 'Attivo' : 'Inattivo'}
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setFieldsEditingCourse(course)}
                      >
                        Campi
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => { setEditingCourse(course); setShowModal(true); }}
                      >
                        Modifica
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <CourseModal
          course={editingCourse}
          onClose={() => setShowModal(false)}
          onSaved={handleSaved}
        />
      )}

      {fieldsEditingCourse && (
        <FieldsEditorModal
          course={fieldsEditingCourse}
          onClose={() => setFieldsEditingCourse(undefined)}
          onSaved={() => { queryClient.invalidateQueries({ queryKey: ['admin-courses'] }); }}
        />
      )}
    </div>
  );
}
